import React from 'react';
import { colors, fonts } from '../patientintel/styles';

// Soft lead-capture gate for /engagement. Mirrors the /intelligence gate
// (src/patientintel/PatientIntelGate.jsx) and logs to the SAME Google Sheet via
// the same webhook — differentiated by `page: 'engagement'`. Uses its own
// 30-day unlock key so the two pages gate independently.
const UNLOCK_KEY = 'rp_engagement_unlock';
const UNLOCK_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const SCROLL_THRESHOLD = 0.20;

const readUnlock = () => {
  try {
    const raw = localStorage.getItem(UNLOCK_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.ts || Date.now() - data.ts > UNLOCK_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const writeUnlock = (data) => {
  try {
    localStorage.setItem(UNLOCK_KEY, JSON.stringify({ ...data, ts: Date.now() }));
  } catch {
    /* storage unavailable — gate re-shows next visit */
  }
};

const getToken = () => {
  try {
    return new URLSearchParams(window.location.search).get('k') || '';
  } catch {
    return '';
  }
};

const postToWebhook = async (payload) => {
  // Defaults to the shared /intelligence sheet; set a dedicated var to split later.
  const url = process.env.REACT_APP_ENGAGEMENT_GATE_WEBHOOK || process.env.REACT_APP_PATIENTINTEL_GATE_WEBHOOK;
  if (!url) return { ok: true, skipped: true };
  const params = new URLSearchParams({
    ...payload,
    token: payload.token || 'none',
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    referrer: document.referrer || '',
    page: 'engagement',
  }).toString();
  try {
    await fetch(`${url}?${params}`, { method: 'GET', mode: 'no-cors' });
    return { ok: true };
  } catch (err) {
    return { ok: false, err };
  }
};

export const logEngagementView = () => {
  const unlock = readUnlock();
  postToWebhook({
    event: 'view',
    token: getToken(),
    known_email: unlock?.email || '',
  });
};

const useGateTrigger = () => {
  const [shouldShow, setShouldShow] = React.useState(false);
  const hasFiredRef = React.useRef(false);

  React.useEffect(() => {
    if (readUnlock()) return undefined;

    const trigger = () => {
      if (hasFiredRef.current) return;
      hasFiredRef.current = true;
      setShouldShow(true);
    };

    const onScroll = () => {
      const h = document.body.scrollHeight - window.innerHeight;
      if (h <= 0) return;
      if (window.scrollY / h >= SCROLL_THRESHOLD) trigger();
    };

    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseleave', onMouseLeave);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return shouldShow;
};

export const EngagementGate = () => {
  const shouldShow = useGateTrigger();
  const [visible, setVisible] = React.useState(false);
  const [first, setFirst] = React.useState('');
  const [last, setLast] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState('idle');
  const firstInputRef = React.useRef(null);
  const token = React.useMemo(getToken, []);

  React.useEffect(() => {
    if (!shouldShow) return undefined;
    setVisible(true);
    document.body.style.overflow = 'hidden';
    setTimeout(() => firstInputRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = '';
    };
  }, [shouldShow]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!first || !last || !company || !email) return;
    setStatus('submitting');
    const { ok } = await postToWebhook({
      event: 'capture',
      first_name: first,
      last_name: last,
      company,
      email,
      token,
    });
    if (!ok) {
      setStatus('error');
      return;
    }
    writeUnlock({ first, last, company, email, token });
    setStatus('unlocked');
    setVisible(false);
    document.body.style.overflow = '';
  };

  if (!visible) return null;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="eng-gate-title" style={overlayStyle}>
      <div style={modalStyle}>
        <div style={coralBarStyle} />
        <p style={eyebrowStyle}>Working With Real Patients</p>
        <h2 id="eng-gate-title" style={headlineStyle}>
          Let's find the right way to work together.
        </h2>
        <p style={subStyle}>Enter your details to access the full overview.</p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input
            ref={firstInputRef} type="text" required placeholder="First name"
            value={first} onChange={(e) => setFirst(e.target.value)}
            autoComplete="given-name" style={inputStyle}
          />
          <input
            type="text" required placeholder="Last name"
            value={last} onChange={(e) => setLast(e.target.value)}
            autoComplete="family-name" style={inputStyle}
          />
          <input
            type="text" required placeholder="Company"
            value={company} onChange={(e) => setCompany(e.target.value)}
            autoComplete="organization" style={inputStyle}
          />
          <input
            type="email" required placeholder="Work email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            autoComplete="email" style={inputStyle}
          />
          <button type="submit" disabled={status === 'submitting'} style={submitStyle}>
            {status === 'submitting' ? 'Continuing…' : 'Continue'}
          </button>
        </form>
        {status === 'error' && (
          <p style={errorStyle}>
            Something went wrong — please try again or{' '}
            <a href="mailto:sbd@realpatients.com" style={{ color: colors.deepCoral }}>email us</a>.
          </p>
        )}
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 10000, display: 'flex',
  alignItems: 'center', justifyContent: 'center', padding: '24px',
  backgroundColor: 'rgba(39,39,38,0.72)', backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)', boxSizing: 'border-box', overflowY: 'auto',
};

const modalStyle = {
  position: 'relative', width: '100%', maxWidth: '460px',
  backgroundColor: colors.white, borderRadius: '16px', padding: '48px 40px 40px',
  boxShadow: '0 24px 60px rgba(0,0,0,0.25)', boxSizing: 'border-box',
};

const coralBarStyle = {
  position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
  backgroundColor: colors.deepCoral, borderRadius: '16px 16px 0 0',
};

const eyebrowStyle = {
  fontFamily: fonts.sans, fontSize: '11px', fontWeight: 600, letterSpacing: '2px',
  textTransform: 'uppercase', color: colors.deepCoral, margin: '0 0 14px',
};

const headlineStyle = {
  fontFamily: fonts.serif, fontSize: '26px', fontWeight: 700,
  color: colors.charcoal, lineHeight: 1.2, margin: '0 0 10px',
};

const subStyle = {
  fontFamily: fonts.sans, fontSize: '15px', color: colors.warmGray,
  lineHeight: 1.6, margin: '0 0 28px',
};

const inputStyle = {
  fontFamily: fonts.sans, fontSize: '15px', padding: '13px 16px',
  borderRadius: '8px', border: `1px solid ${colors.border}`,
  backgroundColor: colors.offWhite, color: colors.charcoal,
  outline: 'none', boxSizing: 'border-box', width: '100%',
};

const submitStyle = {
  fontFamily: fonts.sans, fontSize: '15px', fontWeight: 600,
  padding: '14px 24px', borderRadius: '8px', border: 'none',
  backgroundColor: colors.deepCoral, color: colors.white,
  marginTop: '4px', cursor: 'pointer',
};

const errorStyle = {
  fontFamily: fonts.sans, fontSize: '13px', color: colors.deepCoral,
  marginTop: '14px', textAlign: 'center',
};
